// eslint-disable-next-line no-unused-vars
import {BrickContext, ErrorFlow} from 'olympe';
import {LAMBDA_NAMES} from '../utils/constants';

/**
 * Created by Richard on 13.12.22
 */
/**
 * Singleton class controlling currency
 */
export class CurrencyController {
    /**
     * Return the common currency when multiple exists
     * @return {string}
     */
    static getCommonCurrency() {
        return 'USD';
    }
    /**
     * Check whether all materials have the same currency or not
     * Accept a particular currency to check
     * Accept an optional excludeIdx parameter to exclude a particular material
     * @param {!Array<!Material>} materials
     * @param {string=} currency
     * @param {number=} excludeId
     * @return {boolean}
     */
    static hasDifferentCurrencies(materials, currency, excludeId) {
        const currencyToCheck = currency !== undefined ? currency : materials[0].currency.value;
        return materials.some((material) => {
            if (excludeId !== undefined) {
                // we have to exclude an id
                return material.id !== excludeId && material.currency.value !== currencyToCheck;
            } else {
                return material.currency.value !== currencyToCheck;
            }
        });
    }
    /**
     * Fetch the latest exchange rate from the MD database
     * The returned number is the factor to multiply the `fromCurrency` with to get the converted amount in `toCurrency`
     * @param {string} fromCurrency
     * @param {string} toCurrency
     * @param {string} spenderOrRequesterId
     * @param {BrickContext} context
     * @return {Promise<{rate: number, currency: string}>} exchange rate factor
     */
    static getCurrencyExchangeRate(fromCurrency, toCurrency, spenderOrRequesterId, context) {
        const getRate = context.get(LAMBDA_NAMES.GET_CURRENCY_EXCHANGE_RATE);
        const [controlFlow, fromCurrencyIn, toCurrencyIn, requesterIdIn] = getRate.getInputs();
        const [Success, Error, result] = getRate.getOutputs();
        return new Promise((resolve, reject) => {
            const runner = context.runner(getRate)
                .set(fromCurrencyIn, fromCurrency)
                .set(toCurrencyIn, toCurrency)
                .set(requesterIdIn, spenderOrRequesterId)
                .trigger(controlFlow);
            Promise.any([
                runner.waitFor(Success),
                runner.waitFor(Error),
            ]).then((outputFlow) => {
                if (outputFlow instanceof ErrorFlow) {
                    reject(outputFlow);
                } else {
                    const tuples = JSON.parse(runner.get(result));
                    if (tuples.length < 1) {
                        if (this.countedTimeOfCalling < 2) {
                            this.countedTimeOfCalling++;
                            resolve(this.getCurrencyExchangeRate(toCurrency, fromCurrency, spenderOrRequesterId, context));
                        } else {
                            // eslint-disable-next-line prefer-promise-reject-errors
                            reject({
                                message: ErrorFlow.create(
                                    `No currency rate exchange was found from currency ${fromCurrency} to currency ${toCurrency}`, 500),
                                currency: fromCurrency,
                            });
                            this.countedTimeOfCalling = 0;
                        }
                    } else {
                        const rate = tuples[0]['ExchangeRate'];
                        if (rate.slice(-1) === '-') {
                            resolve({
                                rate: this.countedTimeOfCalling > 0 ? Number.parseFloat(rate.slice(0, -1)) : 1.0 / Number.parseFloat(rate.slice(0, -1)),
                                currency: this.countedTimeOfCalling > 0 ? toCurrency : fromCurrency,
                            });
                            this.countedTimeOfCalling = 0;
                        } else {
                            resolve({
                                rate: this.countedTimeOfCalling > 0 ? 1.0 / Number.parseFloat(rate) : Number.parseFloat(rate),
                                currency: this.countedTimeOfCalling > 0 ? toCurrency : fromCurrency,
                            });
                            this.countedTimeOfCalling = 0;
                        }
                    }
                }
            })
                .finally(() => runner.destroy());
        });
    }
}

CurrencyController.errorMessage = (currency) => {
    return `The total amount of your request cannot be calculated as there is no exchange rate defined for this currency ${currency}. Please contact your IT team to fix it first so you can proceed further with your request.`;
};

CurrencyController.errorMessageOB = (currency) => {
    return `The total amount of your request cannot be calculated as there is no exchange rate defined for this currency ${currency}, hence Competitive Bidding Justification section is displayed. Please attach the relevant documents to proceed with the PR submission.`;
};

CurrencyController.countedTimeOfCalling = 0;
